import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    AlertCircle,
    Component,
    Eye,
    ExternalLink,
    Loader2,
    Pencil,
    RefreshCw,
    Trash2,
    Wrench,
    X,
} from 'lucide-react';
import { API_BASE_URL, API_ENDPOINTS } from '../../constants/api';
import { buildApplicationPomEditorPath } from '../../utils/applicationRoutes';
import { apiFetch } from '../../utils/apiClient';

const BRIDGE_SOURCE = 'testsuite-ai-app';
const EXTENSION_SOURCE = 'testsuite-ai-pom-scanner';

/**
 * Talk to the POM Scanner extension through its content-script bridge.
 *
 * Mirrors the Feature Capture handoff: the app posts to its own window, the
 * bridge relays to the service worker, and the reply comes back the same way.
 * The extension opens the target tab itself so it can arm the scan session with
 * this application, the page URL and the POM being worked on.
 */
export const sendScannerBridgeMessage = (message, timeoutMs = 12000) => {
    if (typeof window === 'undefined') {
        return Promise.reject(new Error('Open TestSuite AI in Chrome to use the POM Scanner.'));
    }

    return new Promise((resolve, reject) => {
        const requestId = `pom-scanner-${Date.now()}-${Math.random().toString(36).slice(2)}`;

        const cleanup = () => {
            window.clearTimeout(timerId);
            window.removeEventListener('message', handleMessage);
        };

        const timerId = window.setTimeout(() => {
            cleanup();
            reject(new Error(
                'POM Scanner extension is not available on this page. Load the extension, '
                + 'refresh TestSuite AI, and try again.'
            ));
        }, timeoutMs);

        const handleMessage = (event) => {
            if (event.source !== window) return;
            const data = event.data || {};
            if (data.source !== EXTENSION_SOURCE || data.requestId !== requestId) return;
            cleanup();
            if (!data.ok) {
                reject(new Error(data.error || 'POM Scanner extension could not open the target.'));
                return;
            }
            resolve(data.response || data);
        };

        window.addEventListener('message', handleMessage);
        window.postMessage({ source: BRIDGE_SOURCE, requestId, ...message }, window.location.origin);
    });
};

const isAbsoluteHttpUrl = (value = '') => {
    try {
        const parsed = new URL(String(value || '').trim());
        return ['http:', 'https:'].includes(parsed.protocol) && Boolean(parsed.host);
    } catch (_err) {
        return false;
    }
};

const healthTone = (state = '') => {
    if (state === 'stale') return 'border-red-200 bg-red-50 text-red-700';
    if (state === 'drifted') return 'border-amber-200 bg-amber-50 text-amber-700';
    return 'border-slate-200 bg-slate-50 text-slate-600';
};

const Badge = ({ children, className = '' }) => (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${className}`}>
        {children}
    </span>
);

const PomDeletingRow = ({ pom, screenshotUrl = '' }) => (
    <div className="grid gap-4 lg:grid-cols-[176px_minmax(0,1fr)_auto] lg:items-center">
        <div className="flex h-28 w-full items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-white opacity-60 lg:w-44">
            {screenshotUrl ? (
                <img
                    src={screenshotUrl}
                    alt=""
                    className="h-full w-full object-contain"
                />
            ) : (
                <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-slate-400">
                    <Component className="h-6 w-6" />
                    <span className="text-xs font-medium">No screenshot</span>
                </div>
            )}
        </div>
        <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
                <h4 className="truncate text-base font-semibold text-slate-900">
                    {pom.pageName}
                </h4>
                <Badge className="border-red-100 bg-red-50 text-red-700">
                    Deleting
                </Badge>
            </div>
            <p className="mt-1 truncate text-sm text-slate-500">{pom.pageUrl}</p>
            <div className="mt-2 flex flex-wrap gap-2 text-xs opacity-60">
                <Badge className="border-slate-200 bg-slate-50 text-slate-700">
                    {pom.locatorCount || 0} locator{pom.locatorCount === 1 ? '' : 's'}
                </Badge>
                <Badge className="border-slate-200 bg-slate-50 text-slate-700">
                    {pom.actionCount || 0} action{pom.actionCount === 1 ? '' : 's'}
                </Badge>
                <Badge className="border-slate-200 bg-slate-50 text-slate-700">
                    {pom.assertionCount || 0} assertion{pom.assertionCount === 1 ? '' : 's'}
                </Badge>
            </div>
        </div>
        <div className="flex justify-start lg:justify-end">
            <span className="inline-flex h-9 items-center gap-2 rounded-lg border border-red-100 bg-white px-3 text-sm font-semibold text-red-700">
                <Loader2 className="h-4 w-4 animate-spin" />
                Deleting...
            </span>
        </div>
    </div>
);

const PomListSkeleton = () => (
    <div className="mt-3 space-y-2" role="status" aria-label="Loading page objects">
        {[0, 1, 2].map((item) => (
            <div
                key={item}
                className="grid animate-pulse gap-4 rounded-xl border border-slate-200 bg-white p-3 shadow-sm lg:grid-cols-[176px_minmax(0,1fr)_auto] lg:items-center"
            >
                <div className="h-28 rounded-lg border border-slate-100 bg-slate-100 lg:w-44" />
                <div className="min-w-0 space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="h-4 w-44 rounded bg-slate-200" />
                        <span className="h-6 w-24 rounded-full bg-teal-100" />
                        <span className="h-6 w-28 rounded-full bg-blue-100" />
                    </div>
                    <span className="block h-3 w-4/5 rounded bg-slate-100" />
                    <div className="flex flex-wrap gap-2">
                        <span className="h-6 w-20 rounded-full bg-slate-100" />
                        <span className="h-6 w-20 rounded-full bg-slate-100" />
                        <span className="h-6 w-24 rounded-full bg-slate-100" />
                        <span className="h-6 w-20 rounded-full bg-slate-100" />
                    </div>
                </div>
                <div className="flex flex-wrap gap-2 lg:justify-end">
                    {[0, 1, 2, 3, 4].map((button) => (
                        <span key={button} className="h-9 w-20 rounded-lg bg-slate-100" />
                    ))}
                </div>
            </div>
        ))}
    </div>
);

const POM_ORIGIN_META = {
    all: { label: 'All', className: 'border-slate-200 bg-white text-slate-700' },
    scanner: { label: 'POM Scanner', className: 'border-blue-200 bg-blue-50 text-blue-700' },
    feature: { label: 'Feature Capture', className: 'border-teal-200 bg-teal-50 text-teal-700' },
    manual: { label: 'Manual', className: 'border-slate-200 bg-slate-50 text-slate-700' },
    other: { label: 'Other', className: 'border-slate-200 bg-white text-slate-600' },
};

const POM_ORIGIN_ORDER = ['all', 'scanner', 'feature', 'manual', 'other'];

const POM_PAGE_SIZE = 10;

const getPomOrigin = (pom = {}) => {
    const source = String(pom?.source || '').trim().toLowerCase();
    const sourceType = String(pom?.source_type || pom?.sourceType || '').trim().toLowerCase();
    const generatedFrom = String(pom?.generated_from || pom?.generatedFrom || '').trim().toLowerCase();
    if (sourceType === 'feature_capture' || source.startsWith('feature_capture') || pom?.feature_capture_id) return 'feature';
    if (source === 'pom_scanner' || generatedFrom === 'chrome_extension') return 'scanner';
    if (source === 'feature_capture' || generatedFrom.includes('feature')) return 'feature';
    if (source === 'metaz_locators_extension' || source === 'manual') return 'manual';
    return source || generatedFrom ? 'other' : 'manual';
};

const buildPomScreenshotUrl = (applicationId, pom = {}) => {
    const assetName = String(pom?.screenshot || '').trim();
    const pageName = String(pom?.pageName || '').trim();
    if (!applicationId || !pageName || !assetName) return '';
    return `${API_BASE_URL}${API_ENDPOINTS.POM_SCANNER_APPLICATION_ASSET(applicationId, pageName, assetName)}`;
};

/**
 * Manage the page objects shared across one application.
 *
 * Every source in the application - Excel, BRD, API, Text - generates scripts
 * from this one catalog, so a page captured here is reusable everywhere.
 */
const buildReturnToSearch = (returnTo = '') => {
    const normalized = String(returnTo || '').trim();
    if (!normalized) return '';
    const params = new URLSearchParams();
    params.set('returnTo', normalized);
    return `?${params.toString()}`;
};

export const ApplicationPomManager = ({
    application,
    onClose,
    closeLabel = 'Close',
    returnTo = '',
    onPOMChange = null,
}) => {
    const applicationId = String(application?.id || '').trim();
    const navigate = useNavigate();
    const onPOMChangeRef = useRef(onPOMChange);

    const [poms, setPoms] = useState([]);
    const [total, setTotal] = useState(0);
    const [originCounts, setOriginCounts] = useState({
        all: 0, scanner: 0, feature: 0, manual: 0, other: 0,
    });
    const [loading, setLoading] = useState(false);
    const [initialized, setInitialized] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [adopted, setAdopted] = useState([]);

    const [scanUrl, setScanUrl] = useState('');
    const [scanPomName, setScanPomName] = useState('');
    const [launching, setLaunching] = useState(false);
    const [busyPoms, setBusyPoms] = useState(new Set());
    const [deletingPoms, setDeletingPoms] = useState(new Set());
    const [pendingDelete, setPendingDelete] = useState('');
    const [selectedPoms, setSelectedPoms] = useState(new Set());
    const [bulkDeleting, setBulkDeleting] = useState(false);
    const [pendingBulkDelete, setPendingBulkDelete] = useState(false);
    const [originFilter, setOriginFilter] = useState('all');
    const [page, setPage] = useState(1);
    const didInitialLoadRef = useRef(false);

    useEffect(() => {
        onPOMChangeRef.current = onPOMChange;
    }, [onPOMChange]);

    const notifyPomChange = useCallback((payload) => {
        const handler = onPOMChangeRef.current;
        if (typeof handler !== 'function') return;
        try {
            Promise.resolve(handler(payload)).catch(() => {});
        } catch (_err) {
            // The catalog itself already loaded; parent refresh failures surface in the parent shell.
        }
    }, []);

    const loadPoms = useCallback(async (options = {}) => {
        if (!applicationId) return;
        const refreshOverview = Boolean(options.refreshOverview);
        const silent = Boolean(options.silent);
        // Legacy per-source POMs are adopted "on first read" per the backend
        // contract; re-running it on every page/filter click would force the
        // slow full-hydration path for no benefit, so only Refresh repeats it.
        const runAdopt = Boolean(options.forceAdopt) || !didInitialLoadRef.current;
        if (!silent) setLoading(true);
        setError('');
        try {
            const response = await apiFetch(API_ENDPOINTS.POM_SCANNER_APPLICATION_POMS(applicationId, {
                adopt: runAdopt,
                fast: !runAdopt,
                origin: originFilter,
                limit: POM_PAGE_SIZE,
                offset: (page - 1) * POM_PAGE_SIZE,
            }));
            if (!response.ok) {
                const payload = await response.json().catch(() => ({}));
                throw new Error(payload?.detail || 'Could not load page objects');
            }
            const payload = await response.json();
            didInitialLoadRef.current = true;
            const nextPoms = Array.isArray(payload?.poms) ? payload.poms : [];
            const nextAdopted = Array.isArray(payload?.adopted) ? payload.adopted : [];
            const nextOriginCounts = payload?.originCounts && typeof payload.originCounts === 'object'
                ? { all: 0, scanner: 0, feature: 0, manual: 0, other: 0, ...payload.originCounts }
                : null;
            const nextTotal = Number.isFinite(payload?.count) ? payload.count : nextPoms.length;
            setPoms(nextPoms);
            setAdopted(nextAdopted);
            setTotal(nextTotal);
            if (nextOriginCounts) setOriginCounts(nextOriginCounts);
            setInitialized(true);
            notifyPomChange({
                poms: nextPoms,
                total: nextOriginCounts ? nextOriginCounts.all : nextTotal,
                adopted: nextAdopted,
                refreshOverview,
            });
        } catch (err) {
            setError(err?.message || 'Could not load page objects');
        } finally {
            if (!silent) setLoading(false);
        }
    }, [applicationId, notifyPomChange, originFilter, page]);

    useEffect(() => { loadPoms(); }, [loadPoms]);

    // A scan happens in another tab, so refresh whenever this one regains focus.
    useEffect(() => {
        const onFocus = () => loadPoms();
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, [loadPoms]);

    // A delete (or a filter switch) can leave `page` past the new last page —
    // step back one page rather than showing a stale empty page.
    useEffect(() => {
        if (initialized && !loading && poms.length === 0 && page > 1) {
            setPage((prev) => Math.max(1, prev - 1));
        }
    }, [poms, loading, page, initialized]);

    // Selections point at a specific page's rows, so a page/filter change
    // invalidates them; a background refresh of the same page should not.
    useEffect(() => {
        setSelectedPoms(new Set());
        setPendingBulkDelete(false);
    }, [page, originFilter]);

    const totalPages = Math.max(1, Math.ceil(total / POM_PAGE_SIZE));
    const initialLoading = loading && !initialized;
    const canEditPageObjects = useMemo(() => {
        if (!Array.isArray(application?.current_user_permissions)) return true;
        const permissions = application.current_user_permissions;
        return permissions.includes('testcases:edit') || permissions.includes('application:manage');
    }, [application?.current_user_permissions]);

    const withPomBusy = useCallback(async (pageName, task) => {
        setBusyPoms((prev) => new Set([...prev, pageName]));
        setError('');
        try {
            await task();
        } catch (err) {
            setError(
                err?.message
                || `The action for “${pageName}” could not be completed. Check the page object details and try again.`
            );
        } finally {
            setBusyPoms((prev) => {
                const next = new Set(prev);
                next.delete(pageName);
                return next;
            });
        }
    }, []);

    const launchScanner = useCallback(async ({ pageUrl, pomName, mode }) => {
        if (!canEditPageObjects) {
            setNotice('You need test case edit permission to scan or update page objects.');
            return;
        }
        const target = String(pageUrl || '').trim();
        if (!isAbsoluteHttpUrl(target)) {
            setNotice('Enter the full http(s) URL of the page you want to scan.');
            return;
        }
        setLaunching(true);
        setNotice('');
        setError('');
        try {
            await sendScannerBridgeMessage({ type: 'lsq-pom-scanner-ping' }, 5000);
            await sendScannerBridgeMessage({
                type: 'lsq-pom-scanner-open',
                applicationId,
                applicationName: String(application?.name || ''),
                pageUrl: target,
                pomName: String(pomName || '').trim(),
                mode: mode === 'existing' ? 'existing' : 'new',
                endpoint: API_BASE_URL,
                appUrl: `${window.location.origin}${window.location.pathname}${window.location.search}`,
            });
            setNotice(
                mode === 'existing'
                    ? `Opened ${target} to update ${pomName}. Scan, review and save in the side panel, `
                      + 'then come back here.'
                    : `Opened ${target} in a new tab. Scan, review and save in the side panel, `
                      + 'then come back here.'
            );
        } catch (err) {
            setNotice(err?.message || 'Could not open the target page.');
        } finally {
            setLaunching(false);
        }
    }, [application?.name, applicationId, canEditPageObjects]);

    const deletePom = useCallback((pageName) => withPomBusy(pageName, async () => {
        if (!canEditPageObjects) {
            setError('You need test case edit permission to delete page objects.');
            return;
        }
        setDeletingPoms((prev) => new Set([...prev, pageName]));
        try {
            const response = await apiFetch(
                `${API_ENDPOINTS.POM_SCANNER_POM(pageName)}?application_id=${encodeURIComponent(applicationId)}`,
                { method: 'DELETE' },
            );
            if (!response.ok) {
                const payload = await response.json().catch(() => ({}));
                throw new Error(payload?.detail || 'Could not delete the page object');
            }
            const payload = await response.json().catch(() => ({}));
            const cleanup = payload?.cleanup || {};
            const removedSelections = Number(cleanup.case_poms_removed || 0) + Number(cleanup.common_poms_removed || 0);
            const staleScripts = Number(cleanup.script_artifacts_updated || 0);
            const materializedCopies = Number(cleanup.materialized_copies_deleted || 0);
            const remainingPoms = poms.filter((pom) => pom.pageName !== pageName);
            setPoms(remainingPoms);
            notifyPomChange({
                poms: remainingPoms,
                total: Math.max(0, (originCounts.all || 0) - 1),
                adopted,
                refreshOverview: false,
            });
            setPendingDelete('');
            setNotice(
                `Deleted ${pageName}.`
                + (removedSelections ? ` Removed ${removedSelections} saved POM reference(s).` : '')
                + (staleScripts ? ` Marked ${staleScripts} script version(s) for regeneration.` : '')
                + (materializedCopies ? ` Removed ${materializedCopies} generated source ${materializedCopies === 1 ? 'copy' : 'copies'}.` : '')
            );
            await loadPoms({ refreshOverview: true, silent: true });
        } finally {
            setDeletingPoms((prev) => {
                const next = new Set(prev);
                next.delete(pageName);
                return next;
            });
        }
    }), [adopted, applicationId, canEditPageObjects, loadPoms, notifyPomChange, originCounts, poms, withPomBusy]);

    const togglePomSelection = useCallback((pageName) => {
        setSelectedPoms((prev) => {
            const next = new Set(prev);
            if (next.has(pageName)) next.delete(pageName); else next.add(pageName);
            return next;
        });
    }, []);

    const allOnPageSelected = poms.length > 0 && poms.every((pom) => selectedPoms.has(pom.pageName));

    const toggleSelectAllOnPage = useCallback(() => {
        setSelectedPoms((prev) => {
            const next = new Set(prev);
            const allSelected = poms.length > 0 && poms.every((pom) => next.has(pom.pageName));
            poms.forEach((pom) => {
                if (allSelected) next.delete(pom.pageName); else next.add(pom.pageName);
            });
            return next;
        });
    }, [poms]);

    const bulkDeletePoms = useCallback(async () => {
        if (!canEditPageObjects || selectedPoms.size === 0) return;
        const pageNames = Array.from(selectedPoms);
        setBulkDeleting(true);
        setError('');
        setDeletingPoms((prev) => new Set([...prev, ...pageNames]));
        try {
            const response = await apiFetch(API_ENDPOINTS.POM_SCANNER_DELETE_BATCH, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ application_id: applicationId, page_names: pageNames }),
            });
            if (!response.ok) {
                const payload = await response.json().catch(() => ({}));
                throw new Error(payload?.detail || 'Could not delete the selected page objects');
            }
            const payload = await response.json();
            const deletedNames = Array.isArray(payload?.deleted) ? payload.deleted : [];
            const failed = Array.isArray(payload?.failed) ? payload.failed : [];
            const deletedSet = new Set(deletedNames);
            const remainingPoms = poms.filter((pom) => !deletedSet.has(pom.pageName));
            setPoms(remainingPoms);
            setSelectedPoms((prev) => {
                const next = new Set(prev);
                deletedSet.forEach((name) => next.delete(name));
                return next;
            });
            setPendingBulkDelete(false);
            notifyPomChange({
                poms: remainingPoms,
                total: Math.max(0, (originCounts.all || 0) - deletedNames.length),
                adopted,
                refreshOverview: false,
            });
            const cleanup = payload?.cleanup || {};
            const removedSelections = Number(cleanup.case_poms_removed || 0) + Number(cleanup.common_poms_removed || 0);
            const staleScripts = Number(cleanup.script_artifacts_updated || 0);
            const materializedCopies = Number(cleanup.materialized_copies_deleted || 0);
            setNotice(
                `Deleted ${deletedNames.length} page object${deletedNames.length === 1 ? '' : 's'}.`
                + (failed.length ? ` ${failed.length} could not be deleted (${failed.map((item) => item.pageName).join(', ')}).` : '')
                + (removedSelections ? ` Removed ${removedSelections} saved POM reference(s).` : '')
                + (staleScripts ? ` Marked ${staleScripts} script version(s) for regeneration.` : '')
                + (materializedCopies ? ` Removed ${materializedCopies} generated source ${materializedCopies === 1 ? 'copy' : 'copies'}.` : '')
            );
            await loadPoms({ refreshOverview: true, silent: true });
        } catch (err) {
            setError(err?.message || 'Could not delete the selected page objects');
        } finally {
            setBulkDeleting(false);
            setDeletingPoms((prev) => {
                const next = new Set(prev);
                pageNames.forEach((name) => next.delete(name));
                return next;
            });
        }
    }, [adopted, applicationId, canEditPageObjects, loadPoms, notifyPomChange, originCounts, poms, selectedPoms]);

    const openPomEditor = useCallback((pom, mode = 'view') => {
        const pageName = String(pom?.pageName || '').trim();
        if (!applicationId || !pageName) return;
        if (mode === 'edit' && !canEditPageObjects) {
            setError('You need test case edit permission to edit page object files.');
            return;
        }
        navigate(`${buildApplicationPomEditorPath(applicationId, pageName, mode)}${buildReturnToSearch(returnTo)}`);
    }, [applicationId, canEditPageObjects, navigate, returnTo]);

    return (
        <section className="application-section">
            <div className="application-section-heading">
                <div>
                    <p className="application-eyebrow">Shared across this application</p>
                    <h2>Page Objects</h2>
                </div>
                {onClose && (
                    <button type="button" onClick={onClose} className="secondary-btn h-9 px-3 text-sm">
                        <X className="h-4 w-4" />
                        {closeLabel}
                    </button>
                )}
            </div>

            <p className="mb-4 text-sm text-slate-600">
                Every testing area in this application generates scripts from these page
                objects. Flow-capture page objects are listed separately and stay tied
                to the captured journey that created them.
            </p>

            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-start gap-3">
                    <span className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-teal-50 text-teal-700">
                        <Wrench className="h-4 w-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                        <h3 className="text-sm font-semibold text-slate-900">Create a page object</h3>
                        <p className="mt-1 text-xs text-slate-600">
                            Enter the page URL. It opens in a new tab with the POM Scanner ready,
                            so you scan, review and save without entering anything again.
                        </p>
                        <div className="mt-3 grid gap-2 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_auto]">
                            <input
                                type="url"
                                value={scanUrl}
                                onChange={(event) => setScanUrl(event.target.value)}
                                placeholder="https://app.example.com/checkout"
                                aria-label="Page URL"
                                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                            />
                            <input
                                type="text"
                                value={scanPomName}
                                onChange={(event) => setScanPomName(event.target.value)}
                                placeholder="POM name (optional)"
                                aria-label="POM name"
                                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                            />
                            <button
                                type="button"
                                onClick={() => launchScanner({
                                    pageUrl: scanUrl,
                                    pomName: scanPomName,
                                    mode: 'new',
                                })}
                                disabled={launching || !canEditPageObjects || !scanUrl.trim()}
                                className="primary-btn h-[38px] whitespace-nowrap px-4 text-sm disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {launching ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
                                Open &amp; Scan
                            </button>
                        </div>
                        {notice && <p className="mt-2 text-xs font-medium text-slate-700">{notice}</p>}
                    </div>
                </div>
            </div>

            {error && (
                <div className="mt-3 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{error}</span>
                </div>
            )}

            {adopted.length > 0 && (
                <div className="mt-3 rounded-xl border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
                    Brought {adopted.length} page object{adopted.length === 1 ? '' : 's'} captured
                    earlier into this shared catalog.
                </div>
            )}

            <div className="mt-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                    {initialLoading ? (
                        <div className="space-y-2" aria-hidden="true">
                            <span className="block h-4 w-36 animate-pulse rounded bg-slate-200" />
                            <div className="flex gap-2">
                                <span className="h-8 w-16 animate-pulse rounded-full bg-slate-200" />
                                <span className="h-8 w-28 animate-pulse rounded-full bg-slate-100" />
                            </div>
                        </div>
                    ) : (
                        <>
                            <p className="text-sm font-semibold text-slate-900">
                                {total} page object{total === 1 ? '' : 's'}
                            </p>
                            <div className="mt-2 flex flex-wrap gap-2">
                                {POM_ORIGIN_ORDER.filter((origin) => origin === 'all' || originCounts[origin] > 0).map((origin) => {
                                    const meta = POM_ORIGIN_META[origin] || POM_ORIGIN_META.other;
                                    const active = originFilter === origin;
                                    return (
                                        <button
                                            key={origin}
                                            type="button"
                                            onClick={() => { setOriginFilter(origin); setPage(1); }}
                                            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                                                active
                                                    ? 'border-slate-900 bg-slate-900 text-white'
                                                    : `${meta.className} hover:bg-slate-50`
                                            }`}
                                        >
                                            <span>{meta.label}</span>
                                            <span className={active
                                                ? 'rounded-full bg-white/15 px-1.5 text-white'
                                                : 'rounded-full bg-slate-100 px-1.5 text-slate-600'}>
                                                {originCounts[origin] || 0}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>
                <button
                    type="button"
                    onClick={() => loadPoms({ forceAdopt: true })}
                    disabled={loading}
                    className="secondary-btn h-8 px-3 text-xs"
                >
                    {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                    Refresh
                </button>
            </div>

            {!initialLoading && canEditPageObjects && poms.length > 0 && (
                <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                    <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
                        <input
                            type="checkbox"
                            checked={allOnPageSelected}
                            onChange={toggleSelectAllOnPage}
                            aria-label="Select all page objects on this page"
                            className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                        {selectedPoms.size > 0 ? `${selectedPoms.size} selected` : 'Select all on this page'}
                    </label>
                    {selectedPoms.size > 0 && (
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={() => { setSelectedPoms(new Set()); setPendingBulkDelete(false); }}
                                disabled={bulkDeleting}
                                className="text-xs font-semibold text-slate-600 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                Clear selection
                            </button>
                            <button
                                type="button"
                                onClick={() => setPendingBulkDelete(true)}
                                disabled={bulkDeleting}
                                className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-sm font-semibold text-red-700 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                <Trash2 className="h-4 w-4" />
                                Delete selected
                            </button>
                        </div>
                    )}
                </div>
            )}

            {pendingBulkDelete && selectedPoms.size > 0 && (
                <div className="mt-3 flex flex-wrap items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
                    <p className="min-w-0 flex-1 text-sm text-red-800">
                        Delete <strong>{selectedPoms.size}</strong> page object{selectedPoms.size === 1 ? '' : 's'}?
                        Scripts already generated against them keep their own copy, but they can no longer be selected.
                    </p>
                    <button
                        type="button"
                        onClick={bulkDeletePoms}
                        disabled={bulkDeleting}
                        className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {bulkDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                        {bulkDeleting ? 'Deleting...' : 'Delete'}
                    </button>
                    <button
                        type="button"
                        onClick={() => setPendingBulkDelete(false)}
                        disabled={bulkDeleting}
                        className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        Cancel
                    </button>
                </div>
            )}

            {initialLoading ? (
                <PomListSkeleton />
            ) : originCounts.all === 0 ? (
                <div className="mt-3 rounded-xl border border-dashed border-slate-300 bg-white py-10 text-center">
                    <Component className="mx-auto h-10 w-10 text-slate-400" />
                    <h4 className="mt-3 text-base font-semibold text-slate-900">No page objects yet</h4>
                    <p className="mt-1 text-sm text-slate-600">
                        Enter a page URL above and click Open &amp; Scan to create the first one.
                    </p>
                </div>
            ) : !total ? (
                <div className="mt-3 rounded-xl border border-dashed border-slate-300 bg-white py-10 text-center">
                    <Component className="mx-auto h-10 w-10 text-slate-400" />
                    <h4 className="mt-3 text-base font-semibold text-slate-900">No page objects in this group</h4>
                </div>
            ) : (
                <div className="mt-3 space-y-2">
                    {poms.map((pom) => {
                        const isBusy = busyPoms.has(pom.pageName);
                        const isDeleting = deletingPoms.has(pom.pageName);
                        const health = pom.health || {};
                        const screenshotUrl = buildPomScreenshotUrl(applicationId, pom);
                        const origin = getPomOrigin(pom);
                        const originMeta = POM_ORIGIN_META[origin] || POM_ORIGIN_META.other;
                        const isFeatureCapture = origin === 'feature';
                        return (
                            <article
                                key={pom.pageName}
                                aria-busy={isDeleting ? 'true' : undefined}
                                className={`rounded-xl border p-3 shadow-sm transition ${
                                    isDeleting
                                        ? 'pointer-events-none border-red-100 bg-red-50/30'
                                        : 'border-slate-200 bg-white hover:border-blue-200 hover:shadow-md'
                                }`}
                            >
                                {isDeleting ? (
                                    <PomDeletingRow pom={pom} screenshotUrl={screenshotUrl} />
                                ) : (
                                    <>
                                        <div className={`grid gap-4 lg:items-center ${canEditPageObjects ? 'lg:grid-cols-[auto_176px_minmax(0,1fr)_auto]' : 'lg:grid-cols-[176px_minmax(0,1fr)_auto]'}`}>
                                    {canEditPageObjects && (
                                        <div className="flex items-start pt-1">
                                            <input
                                                type="checkbox"
                                                checked={selectedPoms.has(pom.pageName)}
                                                onChange={() => togglePomSelection(pom.pageName)}
                                                disabled={isBusy}
                                                aria-label={`Select ${pom.pageName}`}
                                                className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
                                            />
                                        </div>
                                    )}
                                    <div className="flex h-28 w-full items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-50 lg:w-44">
                                        {screenshotUrl ? (
                                            <img
                                                src={screenshotUrl}
                                                alt={`${pom.pageName} page screenshot`}
                                                className="h-full w-full object-contain"
                                                loading="lazy"
                                            />
                                        ) : (
                                            <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-slate-400">
                                                <Component className="h-6 w-6" />
                                                <span className="text-xs font-medium">No screenshot</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <h4 className="truncate text-base font-semibold text-slate-900">
                                                {pom.pageName}
                                            </h4>
                                            <Badge className={originMeta.className}>
                                                {originMeta.label}
                                            </Badge>
                                            {pom.pageState && pom.pageState !== 'default' && (
                                                <Badge className="border-slate-200 bg-white text-slate-600">
                                                    {pom.pageState}
                                                </Badge>
                                            )}
                                            {pom.shared && (
                                                <Badge className="border-slate-200 bg-white text-slate-600">
                                                    Shared component
                                                </Badge>
                                            )}
                                            {health.state && health.state !== 'unknown' && (
                                                <Badge className={healthTone(health.state)}>
                                                    {health.state === 'ok' ? 'Healthy' : health.state}
                                                </Badge>
                                            )}
                                        </div>
                                        <p className="mt-1 truncate text-sm text-slate-600">{pom.pageUrl}</p>
                                        <div className="mt-2 flex flex-wrap gap-2 text-xs">
                                            <Badge className="border-slate-200 bg-slate-50 text-slate-700">
                                                {pom.locatorCount || 0} locator{pom.locatorCount === 1 ? '' : 's'}
                                            </Badge>
                                            <Badge className="border-slate-200 bg-slate-50 text-slate-700">
                                                {pom.actionCount} action{pom.actionCount === 1 ? '' : 's'}
                                            </Badge>
                                            <Badge className="border-slate-200 bg-slate-50 text-slate-700">
                                                {pom.assertionCount} assertion{pom.assertionCount === 1 ? '' : 's'}
                                            </Badge>
                                            <Badge className="border-slate-200 bg-slate-50 text-slate-700">
                                                {pom.functionCount} method{pom.functionCount === 1 ? '' : 's'}
                                            </Badge>
                                        </div>
                                    </div>

                                    <div className="flex flex-wrap gap-2 lg:justify-end">
                                        <button
                                            type="button"
                                            onClick={() => openPomEditor(pom, 'view')}
                                            disabled={isBusy || isDeleting}
                                            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                            <Eye className="h-4 w-4" />
                                            View
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => openPomEditor(pom, 'edit')}
                                            disabled={isBusy || isDeleting || !canEditPageObjects}
                                            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm font-semibold text-slate-700 transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                            <Pencil className="h-4 w-4" />
                                            Edit
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => launchScanner({
                                                pageUrl: pom.pageUrl,
                                                pomName: pom.pageName,
                                                mode: 'existing',
                                            })}
                                            disabled={isBusy || isDeleting || launching || !canEditPageObjects || !isAbsoluteHttpUrl(pom.pageUrl) || isFeatureCapture}
                                            title={isFeatureCapture
                                                ? 'Feature Capture page objects stay tied to their captured journey and cannot be rescanned here.'
                                                : 'Reopen this page to add or refresh its elements'}
                                            className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm font-semibold text-blue-700 transition hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                            <Wrench className="h-4 w-4" />
                                            Update
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setPendingDelete(pom.pageName)}
                                            disabled={isBusy || isDeleting || !canEditPageObjects}
                                            className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-sm font-semibold text-red-700 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                            Delete
                                        </button>
                                    </div>
                                        </div>

                                        {pendingDelete === pom.pageName && (
                                    <div className="mt-3 flex flex-wrap items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
                                        <p className="min-w-0 flex-1 text-sm text-red-800">
                                            Delete <strong>{pom.pageName}</strong>? Scripts already generated
                                            against it keep their own copy, but it can no longer be selected.
                                        </p>
                                        <button
                                            type="button"
                                            onClick={() => deletePom(pom.pageName)}
                                            disabled={isDeleting || !canEditPageObjects}
                                            className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                            {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                                            {isDeleting ? 'Deleting...' : 'Delete'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setPendingDelete('')}
                                            disabled={isDeleting}
                                            className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                        )}
                                    </>
                                )}
                            </article>
                        );
                    })}
                </div>
            )}

            {!initialLoading && total > POM_PAGE_SIZE && (
                <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                    <p className="text-xs text-slate-500">
                        Showing {(page - 1) * POM_PAGE_SIZE + 1}
                        -{Math.min(page * POM_PAGE_SIZE, total)} of {total}
                    </p>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                            disabled={loading || page <= 1}
                            className="secondary-btn h-8 px-3 text-xs disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            Previous
                        </button>
                        <span className="text-xs font-medium text-slate-600">
                            {loading ? <Loader2 className="inline h-3.5 w-3.5 animate-spin" /> : `Page ${page} of ${totalPages}`}
                        </span>
                        <button
                            type="button"
                            onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                            disabled={loading || page >= totalPages}
                            className="secondary-btn h-8 px-3 text-xs disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}
        </section>
    );
};

export default ApplicationPomManager;
